<template lang="html">
    <div
        :id="id"
        class="leaflet-map"
    />
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import mapMarkerHtml from '@foundryspatial/map-marker/mapMarkerHtml';

export default {
    name: 'LeafletMap',

    props: {
        // map element ID
        id: {
            type: String,
            default: 'map',
        },

        // extra leaflet init options
        options: {
            type: Object,
            default: () => ({}),
        },

        // default map center
        center: {
            type: Array,
            // Foundry Spatial office coordinates 😏
            default: () => [48.46622, -123.36111],
        },

        // default zoom
        zoom: {
            type: Number,
            default: 13,
        },

        // map tile layer configuration
        tiles: {
            type: Object,
            default: () => ({
                url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                options: {
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                },
            }),
        },

        // array of geoJSON features
        features: {
            type: Array,
            default: () => [],
        },

        // use feature bounds instead of center/zoom to set the map view
        useBounds: {
            type: Boolean,
            default: true,
        },
    },

    data: () => ({
        // geoJSON feature layer, once created
        featuresLayer: null,
    }),

    mounted() {
        // initialize map with default options
        const map = L.map(this.id, {
            zoomControl: false,
            // enable fractional zooming to make fitBounds work better
            zoomSnap: 0.25,
            // disable animations
            fadeAnimation: false,
            zoomAnimation: false,
            // override default options with prop
            ...this.options,
        });

        // emit an event when the map object is loaded
        map.on('load', (ev) => {
            this.$emit('load', ev, map);
        });

        // create tile layer
        const tilelayer = L.tileLayer(this.tiles.url, this.tiles.options);
        tilelayer.on('load', (ev) => {
            // emit an event when all visible tiles have loaded
            this.$emit('tiles:load', ev, map);
        });

        // add feature layers, if any
        this.addFeatureLayers(map);

        // set view: use center/zoom prop, or feature bounds
        this.setView(map);

        // add tile layer to the map
        map.addLayer(tilelayer);
    },

    methods: {
        /**
         * add geoJSON features to the map from the features prop
         * @param {L.map} map Leaflet map object
         */
        addFeatureLayers(map) {
            // don't create layer if no geoJSON features are passed
            if (this.features.length === 0) return;

            this.featuresLayer = L.geoJSON(this.features, {
                // use custom creation function for point markers
                pointToLayer: this.pointToLayer,
                // apply default styles
                style(feature) {
                    const styleOptions = {
                        weight: 1,
                    };
                    // use thicker weight for LineString features
                    if (feature.geometry.type === 'LineString') {
                        styleOptions.weight = 4;
                    }

                    // apply custom styles from leafletStyles geoJSON property
                    const featureStyles = feature.properties.leafletStyles || {};

                    return {
                        ...styleOptions,
                        ...featureStyles,
                    };
                },
            });
            this.featuresLayer.addTo(map);
        },

        /**
         * set the map view to the bounding box of the features layer,
         * or use the zoom/center props
         * @param {L.map} map Leaflet map object
         */
        setView(map) {
            if (this.useBounds && this.featuresLayer) {
                // fit to geojson feature layer's bounds
                const bounds = this.featuresLayer.getBounds();
                map.fitBounds(bounds);
            } else {
                // use center/zoom props
                map.setView(this.center, this.zoom);
            }
        },

        /**
         * use custom SVG elements as markers
         * @param  {Object} geoJsonPoint geoJSON point feature
         * @param  {L.latLng} latLng     Leaflet latLng coordinates
         * @return {L.marker}            leaflet marker object
         */
        pointToLayer(geoJsonPoint, latLng) {
            // use custom color from markerColor property
            const markerColor = geoJsonPoint.properties.markerColor || '#CCC';

            const svgIcon = L.divIcon({
                html: mapMarkerHtml(markerColor),
                iconSize: [17, 35],
                iconAnchor: [9, 35],
                className: geoJsonPoint.properties.className || '',
            });
            return L.marker(latLng, { icon: svgIcon });
        },
    },
};
</script>
